/* eslint-disable typescript-extended-rules/file-naming-convention */
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

import {
	BeneficiaryReconciliationApplyRequest,
	BeneficiaryReconciliationActiveProcessingResponse,
	BeneficiaryReconciliationComplementResponse,
	BeneficiaryReconciliationDraft,
	BeneficiaryReconciliationDuplicateReturnResponse,
	BeneficiaryReconciliationExportResponse,
	BeneficiaryReconciliationField,
	BeneficiaryReconciliationItemsResponse,
	BeneficiaryReconciliationMappingAnalysisResponse,
	BeneficiaryReconciliationMappingConfirmation,
	BeneficiaryReconciliationProcessingStartResponse,
	BeneficiaryReconciliationProcessingStatusResponse,
	BeneficiaryReconciliationScopeMode,
	BeneficiaryReconciliationSessionsResponse,
	BeneficiaryReconciliationTitularityImportResponse,
	BradescoSpgProductValidationResponse,
	GenericBeneficiaryReconciliationCreateSubPrefill,
	GenericBeneficiaryReconciliationImportModel,
	GenericBeneficiaryReconciliationPreflightExportKind,
	GenericBeneficiaryReconciliationPreflightImportResponse,
	GenericBeneficiaryReconciliationPreflightListStatus,
	GenericBeneficiaryReconciliationPreflightPendenciesResponse,
	GenericBeneficiaryReconciliationPreflightRow,
	GenericBeneficiaryReconciliationPreflightSummary,
	GenericBeneficiaryReconciliationUnidentifiedOrganizationGroupsResponse,
	GenericBeneficiaryReconciliationWalletKind,
} from './models/beneficiary-reconciliation.model';
import { BeneficiaryReconciliationApplyPreviewResponse } from './models/beneficiary-reconciliation-apply-preview-response.model';
import { BeneficiaryReconciliationBulkCardActionAvailabilityResponse } from './models/beneficiary-reconciliation-bulk-card-action-availability-response.model';
import { BeneficiaryReconciliationBulkCardActionRequest } from './models/beneficiary-reconciliation-bulk-card-action-request.model';
import { BeneficiaryReconciliationBulkCardHoldEligibleItemsResponse } from './models/beneficiary-reconciliation-bulk-card-hold-eligible-items-response.model';
import { BeneficiaryReconciliationBulkCardIgnoreResponse } from './models/beneficiary-reconciliation-bulk-card-ignore-response.model';
import { BeneficiaryReconciliationListItemsArgs } from './models/beneficiary-reconciliation-list-items-args.model';

@Injectable({
	providedIn: 'root',
})
export class BeneficiaryReconciliationsService {
	private readonly organizationEndpoint: string = `${environment.apiUrl}/organization`;

	constructor(private readonly http: HttpClient) {}

	analyzeGenericMapping(args: {
		files: File[];
		organizationId: string;
	}): Observable<BeneficiaryReconciliationMappingAnalysisResponse> {
		const formData = new FormData();

		args.files.forEach((file) => {
			formData.append('files', file, file.name);
		});

		return this.http.post<BeneficiaryReconciliationMappingAnalysisResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/mapping/analyze`,
			formData,
		);
	}

	applyGenericSession(args: {
		organizationId: string;
		payload: BeneficiaryReconciliationApplyRequest;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationProcessingStartResponse> {
		return this.http.post<BeneficiaryReconciliationProcessingStartResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/apply`,
			args.payload,
		);
	}

	applyGenericSessionConfirmSuggested(args: {
		fields?: BeneficiaryReconciliationApplyRequest['fields'];
		items?: BeneficiaryReconciliationApplyRequest['items'];
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationProcessingStartResponse> {
		return this.applyGenericSession({
			organizationId: args.organizationId,
			payload: {
				confirmSuggested: true,
				...(args.fields ? { fields: args.fields } : {}),
				...(args.items ? { items: args.items } : {}),
				...(args.scopeMode ? { scopeMode: args.scopeMode } : {}),
			},
			sessionId: args.sessionId,
		});
	}

	compareGenericSession(args: {
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationProcessingStartResponse> {
		return this.http.post<BeneficiaryReconciliationProcessingStartResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/compare`,
			{
				...(args.scopeMode ? { scopeMode: args.scopeMode } : {}),
			},
		);
	}

	complementGenericMissingData(args: {
		file: File;
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationComplementResponse> {
		const formData = new FormData();

		formData.append('complementFile', args.file, args.file.name);

		return this.http.post<BeneficiaryReconciliationComplementResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/missing-data-complement`,
			formData,
		);
	}

	confirmGenericMapping(args: {
		files: File[];
		mapping: BeneficiaryReconciliationMappingConfirmation;
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): Observable<{
		preflight: GenericBeneficiaryReconciliationPreflightSummary;
		sessionId: string;
	}> {
		const formData = new FormData();

		args.files.forEach((file) => {
			formData.append('files', file, file.name);
		});
		formData.append('mapping', JSON.stringify(args.mapping));
		BeneficiaryReconciliationsService.appendScopeMode(formData, args.scopeMode);

		return this.http.post<{
			preflight: GenericBeneficiaryReconciliationPreflightSummary;
			sessionId: string;
		}>(
			`${this.getGenericEndpoint(args.organizationId)}/mapping/confirm`,
			formData,
		);
	}

	createAmilPreflight(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): Observable<{
		preflight: GenericBeneficiaryReconciliationPreflightSummary;
		sessionId: string;
	}> {
		const formData =
			BeneficiaryReconciliationsService.buildHealthDentalFormData(args);

		return this.http.post<{
			preflight: GenericBeneficiaryReconciliationPreflightSummary;
			sessionId: string;
		}>(
			`${this.getGenericEndpoint(args.organizationId)}/preflight/amil`,
			formData,
		);
	}

	createBradescoPreflight(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): Observable<{
		preflight: GenericBeneficiaryReconciliationPreflightSummary;
		sessionId: string;
	}> {
		const formData =
			BeneficiaryReconciliationsService.buildHealthDentalFormData(args);

		return this.http.post<{
			preflight: GenericBeneficiaryReconciliationPreflightSummary;
			sessionId: string;
		}>(
			`${this.getGenericEndpoint(args.organizationId)}/preflight/bradesco`,
			formData,
		);
	}

	createBradescoSpgPreflight(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): Observable<{
		preflight: GenericBeneficiaryReconciliationPreflightSummary;
		sessionId: string;
	}> {
		const formData =
			BeneficiaryReconciliationsService.buildHealthDentalFormData(args);

		return this.http.post<{
			preflight: GenericBeneficiaryReconciliationPreflightSummary;
			sessionId: string;
		}>(
			`${this.getGenericEndpoint(args.organizationId)}/preflight/bradesco-spg`,
			formData,
		);
	}

	createGenericPreflight(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): Observable<{
		preflight: GenericBeneficiaryReconciliationPreflightSummary;
		sessionId: string;
	}> {
		const formData =
			BeneficiaryReconciliationsService.buildHealthDentalFormData(args);

		return this.http.post<{
			preflight: GenericBeneficiaryReconciliationPreflightSummary;
			sessionId: string;
		}>(`${this.getGenericEndpoint(args.organizationId)}/preflight`, formData);
	}

	deleteGenericDraft(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<{ deleted: true }> {
		return this.http.delete<{ deleted: true }>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/draft`,
		);
	}

	downloadGenericOriginalFile(args: {
		fileId: string;
		organizationId: string;
		sessionId: string;
	}): Observable<HttpResponse<Blob>> {
		return this.http.get(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/files/${encodeURIComponent(args.fileId)}/download`,
			{
				observe: 'response',
				responseType: 'blob',
			},
		);
	}

	exportGenericActiveMissingInDb(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationExportResponse> {
		return this.http.get<BeneficiaryReconciliationExportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/active-missing-in-db/export`,
		);
	}

	exportGenericDuplicateInFile(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationExportResponse> {
		return this.http.get<BeneficiaryReconciliationExportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/duplicate-in-file/export`,
		);
	}

	exportGenericHistory(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationExportResponse> {
		return this.http.get<BeneficiaryReconciliationExportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/history/export`,
		);
	}

	exportGenericOperationalDivergence(args: {
		field: BeneficiaryReconciliationField;
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationExportResponse> {
		return this.http.get<BeneficiaryReconciliationExportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/operational-report/${args.field}/export`,
		);
	}

	exportGenericPreflightPendencies(args: {
		kind: GenericBeneficiaryReconciliationPreflightExportKind;
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationExportResponse> {
		const path =
			args.kind === 'UNCOMPARED_LINES'
				? 'uncompared-lines'
				: 'unidentified-organizations';

		return this.http.get<BeneficiaryReconciliationExportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/${path}/export`,
		);
	}

	getGenericActiveProcessing(args: {
		organizationId: string;
	}): Observable<BeneficiaryReconciliationActiveProcessingResponse> {
		return this.http.get<BeneficiaryReconciliationActiveProcessingResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/processing/active`,
		);
	}

	getGenericBulkCardActionAvailability(args: {
		organizationId: string;
		payload: BeneficiaryReconciliationBulkCardActionRequest;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationBulkCardActionAvailabilityResponse> {
		return this.http.post<BeneficiaryReconciliationBulkCardActionAvailabilityResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/bulk-card-actions/availability`,
			args.payload,
		);
	}

	getGenericBulkCardHoldEligibleItems(args: {
		organizationId: string;
		payload: BeneficiaryReconciliationBulkCardActionRequest;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationBulkCardHoldEligibleItemsResponse> {
		return this.http.post<BeneficiaryReconciliationBulkCardHoldEligibleItemsResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/bulk-card-actions/hold-eligible-items`,
			args.payload,
		);
	}

	getGenericCreateSubPrefill(args: {
		cnpj: string;
		organizationId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationCreateSubPrefill> {
		return this.http.post<GenericBeneficiaryReconciliationCreateSubPrefill>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/unknown-cnpjs/${encodeURIComponent(
				args.cnpj,
			)}/create-sub-prefill`,
			{},
		);
	}

	getGenericDraft(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationDraft | null> {
		return this.http.get<BeneficiaryReconciliationDraft | null>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/draft`,
		);
	}

	getGenericProcessingStatus(args: {
		organizationId: string;
		processingId: string;
	}): Observable<BeneficiaryReconciliationProcessingStatusResponse> {
		return this.http.get<BeneficiaryReconciliationProcessingStatusResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/processing/${
				args.processingId
			}/status`,
		);
	}

	holdGenericPreflightPendency(args: {
		organizationId: string;
		rowId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationPreflightRow> {
		return this.http.patch<GenericBeneficiaryReconciliationPreflightRow>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/pendencies/${encodeURIComponent(args.rowId)}/hold`,
			{},
		);
	}

	ignoreGenericBulkCardEligibleItems(args: {
		organizationId: string;
		payload: BeneficiaryReconciliationBulkCardActionRequest;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationBulkCardIgnoreResponse> {
		return this.http.post<BeneficiaryReconciliationBulkCardIgnoreResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/bulk-card-actions/ignore`,
			args.payload,
		);
	}

	ignoreGenericPreflightPendency(args: {
		organizationId: string;
		rowId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationPreflightRow> {
		return this.http.patch<GenericBeneficiaryReconciliationPreflightRow>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/pendencies/${encodeURIComponent(args.rowId)}/ignore`,
			{},
		);
	}

	importGenericDuplicateReturn(args: {
		file: File;
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationDuplicateReturnResponse> {
		const formData = new FormData();

		formData.append('duplicateReturnFile', args.file, args.file.name);

		return this.http.post<BeneficiaryReconciliationDuplicateReturnResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/duplicate-return`,
			formData,
		);
	}

	importGenericPreflightPendencies(args: {
		file: File;
		organizationId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationPreflightImportResponse> {
		const formData = new FormData();

		formData.append('preflightReturnFile', args.file, args.file.name);

		return this.http.post<GenericBeneficiaryReconciliationPreflightImportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/pendencies/import`,
			formData,
		);
	}

	importGenericTitularityReturn(args: {
		file: File;
		organizationId: string;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationTitularityImportResponse> {
		const formData = new FormData();

		formData.append('titularityReturnFile', args.file, args.file.name);

		return this.http.post<BeneficiaryReconciliationTitularityImportResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/operational-report/TITULARITY_MISMATCH/import`,
			formData,
		);
	}

	listGenericItems(
		args: BeneficiaryReconciliationListItemsArgs & { organizationId: string },
	): Observable<BeneficiaryReconciliationItemsResponse> {
		let params = new HttpParams()
			.set('limit', String(args.limit))
			.set('page', String(args.page));

		if (args.field) {
			params = params.set('field', args.field);
		}

		if (args.status) {
			params = params.set('status', args.status);
		}

		if (args.reviewState) {
			params = params.set('reviewState', args.reviewState);
		}

		if (args.carrier?.trim()) {
			params = params.set('carrier', args.carrier.trim());
		}

		if (args.search?.trim()) {
			params = params.set('search', args.search.trim());
		}

		return this.http.get<BeneficiaryReconciliationItemsResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.comparisonId
			}/items`,
			{ params },
		);
	}

	listGenericPreflightPendencies(args: {
		kind?: GenericBeneficiaryReconciliationPreflightExportKind;
		limit: number;
		organizationId: string;
		page: number;
		sessionId: string;
		status: GenericBeneficiaryReconciliationPreflightListStatus;
	}): Observable<GenericBeneficiaryReconciliationPreflightPendenciesResponse> {
		let params = new HttpParams()
			.set('limit', String(args.limit))
			.set('page', String(args.page))
			.set('status', args.status);

		if (args.kind) {
			params = params.set('kind', args.kind);
		}

		return this.http.get<GenericBeneficiaryReconciliationPreflightPendenciesResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/pendencies`,
			{ params },
		);
	}

	listGenericSessions(args: {
		limit: number;
		organizationId: string;
		page: number;
		scopeMode?: BeneficiaryReconciliationScopeMode;
		sessionId?: string;
	}): Observable<BeneficiaryReconciliationSessionsResponse> {
		let params = new HttpParams()
			.set('limit', String(args.limit))
			.set('page', String(args.page));

		if (args.scopeMode) {
			params = params.set('scopeMode', args.scopeMode);
		}

		if (args.sessionId) {
			params = params.set('sessionId', args.sessionId);
		}

		return this.http.get<BeneficiaryReconciliationSessionsResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions`,
			{ params },
		);
	}

	listGenericUnidentifiedOrganizationGroups(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationUnidentifiedOrganizationGroupsResponse> {
		return this.http.get<GenericBeneficiaryReconciliationUnidentifiedOrganizationGroupsResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/unidentified-organizations`,
		);
	}

	previewGenericSessionApply(args: {
		fields?: BeneficiaryReconciliationApplyRequest['fields'];
		items?: BeneficiaryReconciliationApplyRequest['items'];
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
		sessionId: string;
	}): Observable<BeneficiaryReconciliationApplyPreviewResponse> {
		return this.http.post<BeneficiaryReconciliationApplyPreviewResponse>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/apply/preview`,
			{
				...(args.fields ? { fields: args.fields } : {}),
				...(args.items ? { items: args.items } : {}),
				...(args.scopeMode ? { scopeMode: args.scopeMode } : {}),
			},
		);
	}

	replaceGenericPreflightBase(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		importModel: GenericBeneficiaryReconciliationImportModel;
		organizationId: string;
		removedWalletKinds?: GenericBeneficiaryReconciliationWalletKind[];
		scopeMode?: BeneficiaryReconciliationScopeMode;
		sessionId: string;
	}): Observable<{
		preflight: GenericBeneficiaryReconciliationPreflightSummary;
		sessionId: string;
	}> {
		const formData =
			BeneficiaryReconciliationsService.buildHealthDentalFormData(args);

		formData.append('importModel', args.importModel);
		args.removedWalletKinds?.forEach((walletKind) => {
			formData.append('removedWalletKinds', walletKind);
		});

		return this.http.post<{
			preflight: GenericBeneficiaryReconciliationPreflightSummary;
			sessionId: string;
		}>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/replace-base`,
			formData,
		);
	}

	reprocessGenericPreflight(args: {
		organizationId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationPreflightSummary> {
		return this.http.patch<GenericBeneficiaryReconciliationPreflightSummary>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/reprocess`,
			{},
		);
	}

	resolveGenericPreflightPendency(args: {
		carrier?: string;
		cpf?: string;
		organizationId: string;
		rowId: string;
		sessionId: string;
		treatAsMatrix?: boolean;
	}): Observable<GenericBeneficiaryReconciliationPreflightRow> {
		return this.http.patch<GenericBeneficiaryReconciliationPreflightRow>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/pendencies/${encodeURIComponent(args.rowId)}/resolve`,
			{
				carrier: args.carrier,
				cpf: args.cpf,
				treatAsMatrix: args.treatAsMatrix,
			},
		);
	}

	resumeGenericPreflightPendency(args: {
		organizationId: string;
		rowId: string;
		sessionId: string;
	}): Observable<GenericBeneficiaryReconciliationPreflightRow> {
		return this.http.patch<GenericBeneficiaryReconciliationPreflightRow>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/pendencies/${encodeURIComponent(args.rowId)}/resume`,
			{},
		);
	}

	saveGenericDraft(args: {
		draft: BeneficiaryReconciliationDraft;
		organizationId: string;
		sessionId: string;
	}): Observable<{ saved: true }> {
		return this.http.put<{ saved: true }>(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/draft`,
			args.draft,
		);
	}

	treatUnknownCnpjAsMatrix(args: {
		cnpj: string;
		organizationId: string;
		sessionId: string;
	}): Observable<unknown> {
		return this.http.patch(
			`${this.getGenericEndpoint(args.organizationId)}/sessions/${
				args.sessionId
			}/preflight/unknown-cnpjs/${encodeURIComponent(
				args.cnpj,
			)}/treat-as-matrix`,
			{},
		);
	}

	validateBradescoSpgProducts(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		organizationId: string;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): Observable<BradescoSpgProductValidationResponse> {
		const formData =
			BeneficiaryReconciliationsService.buildHealthDentalFormData(args);

		return this.http.post<BradescoSpgProductValidationResponse>(
			`${this.getGenericEndpoint(
				args.organizationId,
			)}/preflight/bradesco-spg/product-validation`,
			formData,
		);
	}

	private getGenericEndpoint(organizationId: string): string {
		return `${this.organizationEndpoint}/${organizationId}/beneficiary-reconciliations`;
	}

	private static appendScopeMode(
		formData: FormData,
		scopeMode?: BeneficiaryReconciliationScopeMode,
	): void {
		if (scopeMode) {
			formData.append('scopeMode', scopeMode);
		}
	}

	private static buildHealthDentalFormData(args: {
		dentalFile?: File | null;
		healthFile?: File | null;
		scopeMode?: BeneficiaryReconciliationScopeMode;
	}): FormData {
		const formData = new FormData();

		if (args.healthFile) {
			formData.append('healthFile', args.healthFile, args.healthFile.name);
		}

		if (args.dentalFile) {
			formData.append('dentalFile', args.dentalFile, args.dentalFile.name);
		}

		BeneficiaryReconciliationsService.appendScopeMode(formData, args.scopeMode);

		return formData;
	}
}
